import { Link, useSearchParams } from 'react-router-dom';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/Animations';
import { McIcon } from '../components/CraftingGrid';

const templateurl = 'https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_1.mp4';

const wiki_nav = [
  { label: 'Home', to: '/wiki' },
  { label: 'Custom Items', to: '/wiki/items' },
  { label: 'Custom Mobs', to: '/wiki/mobs' },
];

const raritycolor = {
  common:    '#9e9e9e',
  uncommon:  '#52c078',
  rare:      '#5a9ee0',
  elite:     '#a070e0',
  epic:      '#c060d0',
  legendary: '#f0ab20',
  boss:      '#e84c3d',
  unique:    '#00e5c8',
};

const RARITY_RANK = { unique: 6, legendary: 5, epic: 4, elite: 3, rare: 2, uncommon: 1, common: 0 };

const MOB_TIER_LABEL = { 1: 'Tier 1', 2: 'Tier 2', 3: 'Tier 3', 4: 'Tier 4' };
const MOB_TIER_COLOR = { 1: '#9e9e9e', 2: '#52c078', 3: '#5a9ee0', 4: '#e84c3d' };

function SearchResults({ query, onClear, items, mobs }) {
  const q = query.toLowerCase().trim();
  const itemResults = items.filter(i =>
    i.name.toLowerCase().includes(q) ||
    i.type?.toLowerCase().includes(q) ||
    i.category?.toLowerCase().includes(q)
  );
  const mobResults = mobs.filter(m =>
    m.name.toLowerCase().includes(q) ||
    m.type?.toLowerCase().includes(q)
  );
  const total = itemResults.length + mobResults.length;

  return (
    <div className="wh-search-results">
      <div className="wh-search-header">
        <span className="wh-search-count">{total} result{total !== 1 ? 's' : ''} for "{query}"</span>
        <button className="wh-search-clear" onClick={onClear}>Clear</button>
      </div>

      {total === 0 && (
        <div className="wh-search-empty">No items or mobs match that search.</div>
      )}

      {itemResults.length > 0 && (
        <div className="wh-search-section">
          <div className="wh-section-head">
            <span className="wh-section-head-text">Items ({itemResults.length})</span>
          </div>
          <StaggerContainer className="wh-result-list">
            {itemResults.map(item => (
              <StaggerItem key={item.id}>
                <Link to={`/wiki/item/${item.slug}`} className="wh-result-row">
                  <div className="wh-result-info">
                    <span className="wh-result-name">{item.name}</span>
                    <span className="wh-result-meta">{item.category} · {item.type}</span>
                  </div>
                  <span
                    className="wh-result-rarity"
                    style={{ color: raritycolor[item.rarity] || '#9e9e9e' }}
                  >
                    {item.rarity}
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      )}

      {mobResults.length > 0 && (
        <div className="wh-search-section">
          <div className="wh-section-head">
            <span className="wh-section-head-text">Mobs ({mobResults.length})</span>
          </div>
          <StaggerContainer className="wh-result-list">
            {mobResults.map(mob => (
              <StaggerItem key={mob.id}>
                <Link to={`/wiki/mob/${mob.slug}`} className="wh-result-row">
                  <div className="wh-result-info">
                    <span className="wh-result-name">{mob.name}</span>
                    <span className="wh-result-meta">{mob.type}</span>
                  </div>
                  <span
                    className="wh-result-rarity"
                    style={{ color: raritycolor[mob.tier] || '#9e9e9e' }}
                  >
                    {mob.tier}
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      )}
    </div>
  );
}

export default function WikiHome({ items = [], mobs = [] }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';

  const clearSearch = () => setSearchParams({});

  const enabledItems = items.filter(i => i.enabled !== 0 && i.enabled !== '0');
  const enabledMobs  = mobs.filter(m => m.enabled !== 0 && m.enabled !== '0');

  // Top 10 enabled items sorted by rarity rank
  const featuredItems = [...enabledItems]
    .sort((a, b) => (RARITY_RANK[b.rarity] ?? 0) - (RARITY_RANK[a.rarity] ?? 0))
    .slice(0, 10);

  // Tier 3+ enabled mobs for featured section
  const featuredMobs = enabledMobs
    .filter(m => (m.tier ?? 1) >= 3)
    .slice(0, 6);

  const item_prev = enabledItems.slice(0, 5).map(i => i.name);
  const mobs_prev = enabledMobs.slice(0, 5).map(m => m.name);

  const catogories = [
    {
      title: 'Custom Items',
      desc: 'All custom weapons, tools and armour — stats, crafting recipes, and special abilities.',
      color: '#e84c3d',
      link: '/wiki/items',
      previews: item_prev,
      total: enabledItems.length,
    },
    {
      title: 'Custom Mobs',
      desc: 'Unique creatures with custom drops. Spawn conditions, drop tables and combat tips.',
      color: '#a070e0',
      link: '/wiki/mobs',
      previews: mobs_prev,
      total: enabledMobs.length,
    },
  ];

  return (
    <>
      <div className="wh-video-hero">
        <video className="wh-video-hero-vid" src={templateurl} autoPlay loop muted playsInline />
        <div className="wh-video-hero-overlay" />
        <FadeIn className="wh-video-hero-content" y={30} duration={0.8}>
          <div className="wh-eyebrow">Community Wiki</div>
          <h1 className="wh-title">Simple<em>MC</em> Wiki</h1>
          <p className="wh-subtitle">
            Everything you need to know about SimpleMC, in one place
          </p>
        </FadeIn>
      </div>

      <div className="wh-body">
        <aside className="wh-sidebar">
          <div className="wh-sidebar-group">
            <div className="wh-sidebar-label">Navigation</div>
            {wiki_nav.map(item => (
              <Link
                key={item.label}
                to={item.to}
                className={`wh-sidebar-link${item.soon ? ' wh-sidebar-link-soon' : ''}`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </aside>

        <main className="wh-main">
          {query ? (
            <SearchResults query={query} onClear={clearSearch} items={items} mobs={mobs} />
          ) : (
            <>
              <div className="wh-section-head">
                <span className="wh-section-head-text">Browse by Category</span>
              </div>

              <StaggerContainer className="wh-dir-list">
                {catogories.map(cat => {
                  const Tag = cat.soon ? 'div' : Link;
                  return (
                    <StaggerItem key={cat.title}>
                      <Tag
                        to={cat.link}
                        className="wh-dir-row"
                        data-soon={cat.soon ? '' : undefined}
                      >
                        <div
                          className="wh-dir-accent"
                          style={{ background: cat.color }}
                        />
                        <div className="wh-dir-content">
                          <div className="wh-dir-title-row">
                            <span className="wh-dir-title">{cat.title}</span>
                            {cat.soon && <span className="wh-cat-pill wh-cat-pill-soon">Soon</span>}
                          </div>
                          <div className="wh-dir-desc">{cat.desc}</div>
                          {cat.previews?.length > 0 && (
                            <div className="wh-preview-chips">
                              {cat.previews.map(name => (
                                <span key={name} className="wh-preview-chip">{name}</span>
                              ))}
                              {cat.total > cat.previews.length && (
                                <span className="wh-preview-more">+{cat.total - cat.previews.length} more</span>
                              )}
                            </div>
                          )}
                        </div>
                        {!cat.soon && <div className="wh-dir-arrow">›</div>}
                      </Tag>
                    </StaggerItem>
                  );
                })}
              </StaggerContainer>

              <div className="wh-section-head">
                <span className="wh-section-head-text">Quick Links</span>
              </div>
              <div className="wh-quicklinks">
                <Link to="/" className="wh-quicklink">Main Website</Link>
                <Link to="/apply" className="wh-quicklink">Apply for Staff</Link>
                <Link to="/giveaways" className="wh-quicklink">Giveaways</Link>
                <Link to="/blogs" className="wh-quicklink">Blogs</Link>
                <Link to="/our-staff" className="wh-quicklink">Our Staff</Link>
              </div>

              {featuredItems.length > 0 && (
                <>
                  <div className="wh-section-head" style={{ marginTop: '2rem' }}>
                    <span className="wh-section-head-text">Featured Items</span>
                    <Link to="/wiki/items" className="wh-section-head-link">View all →</Link>
                  </div>
                  <StaggerContainer className="wh-featured-grid">
                    {featuredItems.map(item => (
                      <StaggerItem key={item.id ?? item.slug}>
                        <Link to={`/wiki/item/${item.slug}`} className="wh-featured-card">
                          <div className="wh-featured-icon">
                            <McIcon materialKey={item.material || item.type || 'barrier'} size={36} />
                          </div>
                          <div className="wh-featured-info">
                            <span className="wh-featured-name">{item.name}</span>
                            <span className="wh-featured-category">{item.category || '—'}</span>
                          </div>
                          <span
                            className="wh-featured-rarity"
                            style={{ color: raritycolor[item.rarity] || '#9e9e9e' }}
                          >{item.rarity}</span>
                        </Link>
                      </StaggerItem>
                    ))}
                  </StaggerContainer>
                </>
              )}

              {featuredMobs.length > 0 && (
                <>
                  <div className="wh-section-head" style={{ marginTop: '2rem' }}>
                    <span className="wh-section-head-text">Strong Mobs</span>
                    <Link to="/wiki/mobs" className="wh-section-head-link">View all →</Link>
                  </div>
                  <StaggerContainer className="wh-featured-grid">
                    {featuredMobs.map(mob => (
                      <StaggerItem key={mob.id ?? mob.slug}>
                        <div className="wh-featured-card">
                          <span
                            className="wh-featured-rarity-dot"
                            style={{ background: MOB_TIER_COLOR[mob.tier] || '#9e9e9e' }}
                          />
                          <span className="wh-featured-name">{mob.name}</span>
                          <span
                            className="wh-featured-rarity"
                            style={{ color: MOB_TIER_COLOR[mob.tier] || '#9e9e9e' }}
                          >{MOB_TIER_LABEL[mob.tier] || `Tier ${mob.tier}`}</span>
                        </div>
                      </StaggerItem>
                    ))}
                  </StaggerContainer>
                </>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
}
